<template>
  <div class="flex">
    <lf-dropdown
      class="h-10 w-12 flex-none"
      :options="fieldTypeOptions"
      :value="localState.type"
      :toggle-from-parent="showFieldTypeDropdown"
      :teleport-options="{
        enabled: true,
        horizontalScrollable: 'offer-fields-modal',
      }"
      custom-z-index="z-999"
      v-on-click-outside="() => (showFieldTypeDropdown = false)"
      menu-width="w-32"
    >
      <template #toggle-button>
        <div
          class="flex h-10 w-12 items-center justify-center space-x-1 rounded-l border"
          :class="[
            showFieldTypeDropdown
              ? 'z-10 border border-blue-600 bg-blue-50 ring-1 ring-blue-600'
              : 'border-r-0 border-gray-200 bg-gray-50',
            isCustomField && canChange ? 'cursor-pointer' : 'cursor-inherit',
            {
              'hover:border-r hover:border-gray-300':
                isCustomField && !showFieldTypeDropdown,
            },
          ]"
          @click="toggleDropdown"
        >
          <component :is="offerFieldIcons[localState.type]" />
          <span
            v-if="isCustomField && canChange"
            class="flex items-center text-gray-400"
          >
            <icon-base
              class="transform transition-all"
              height="6"
              width="10"
              icon="dropdown-arrow"
              :class="{ 'rotate-180': showFieldTypeDropdown }"
            />
          </span>
        </div>
      </template>
      <template #listItem="{ option }">
        <div
          class="flex cursor-pointer items-center space-x-1 p-1 px-2 hover:bg-gray-100"
          @click="handleFieldTypeChange(option as FieldType)"
        >
          <component :is="offerFieldIcons[option as FieldType]" />
          <span>{{ option }}</span>
        </div>
      </template>
    </lf-dropdown>
    <div class="group relative w-full">
      <div
        v-if="canChange"
        v-tooltip="$t('OFFER_CUSTOMISATION.REMOVE_FIELD')"
        class="absolute top-[12px] right-0 z-100 flex h-4 w-4 translate-x-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-400 opacity-0 group-hover:opacity-100"
        @click.prevent="removeField"
      >
        <icon-base
          icon="x"
          height="10"
          width="10"
          viewBox="0 0 16 16"
          class="-mt-[2px] text-white"
        />
      </div>
      <lf-input
        name="offer-field"
        class="h-10 font-normal"
        :value="localState[offerTemplateView].label"
        :placeholder="activePlaceholder"
        :show-label="false"
        :disabled="!canChange"
        @blur="updateFieldLabel($event.target.value)"
      >
        <template #suffix>
          <div class="flex">
            <div
              v-for="(item, index) in visibilityControlsOptions"
              :key="index"
              v-tooltip="item.tooltip"
              class="mx-1 cursor-pointer rounded-full bg-blue-100 px-1 py-[5px] text-xxxs leading-none font-medium"
              :class="
                !!localState[offerTemplateView].visibility?.[item.key]
                  ? 'bg-blue-100 text-blue-600/80'
                  : 'bg-gray-100 text-gray-600/80'
              "
              @click="item.clickHandler"
            >
              {{ item.label }}
            </div>
          </div>
        </template>
      </lf-input>
    </div>
  </div>
</template>
<script setup lang="ts">
import { type Ref, computed, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { OfferTemplateView, VisibilityControl } from "@/enums/offerTemplates";
import { FieldType } from "@/enums/offers";
import { offerFieldIcons } from "@/helpers/constants/offers";

import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

import type { OfferTemplateField } from "@/models/offers";

const props = withDefaults(
  defineProps<{
    offerField: OfferTemplateField;
    canChange?: boolean;
  }>(),
  { canChange: true }
);

const emit = defineEmits<{
  updateField: [OfferTemplateField];
  removeField: [];
}>();

const offerTemplateView = inject<
  Ref<OfferTemplateView.OFFER | OfferTemplateView.CLOSING>
>("offerTemplateView", ref(OfferTemplateView.OFFER));

const { getters } = useStore();

const { t } = useI18n();

const CUSTOM_FIELD_DEFAULT_NAME = t("COMMON.CUSTOM_FIELD");

const placeholders = ref({
  [OfferTemplateView.OFFER]: props.offerField.offer.label,
  [OfferTemplateView.CLOSING]: props.offerField.closing.label,
});

const activePlaceholder = computed(() => {
  return placeholders.value[offerTemplateView.value];
});

const localState = ref(props.offerField);

const showFieldTypeDropdown = ref(false);

const isCustomField = computed(() => props.offerField.name.includes("custom"));

const fieldTypeOptions = computed<Record<string, string>>(() => {
  const offerFieldTypes = getters["options/offerFieldTypes"] as string[];
  return offerFieldTypes.reduce((acc: Record<string, string>, type) => {
    if (
      type !== FieldType.OPTIONS ||
      !props.offerField.name?.includes("custom")
    ) {
      acc[type] = type;
    }
    return acc;
  }, {});
});

const handleFieldTypeChange = (option: FieldType) => {
  showFieldTypeDropdown.value = false;
  localState.value.type = option;
  emit("updateField", localState.value);
};

const toggleDropdown = () => {
  if (!isCustomField.value || !props.canChange) {
    return;
  }
  showFieldTypeDropdown.value = !showFieldTypeDropdown.value;
};

const removeField = () => {
  emit("removeField");
};

const toggleVisibility = (control: VisibilityControl) => {
  const currentView = localState.value[offerTemplateView.value];
  const currentVisibility = {
    workflow_live: currentView.visibility?.workflow_live ?? false,
    borrower_platform: currentView.visibility?.borrower_platform ?? false,
  };

  const updatedVisibility = {
    workflow_live:
      control === VisibilityControl.workflow_live
        ? !currentVisibility.workflow_live
        : currentVisibility.workflow_live,
    borrower_platform:
      control === VisibilityControl.borrower_platform
        ? !currentVisibility.borrower_platform
        : currentVisibility.borrower_platform,
  };

  localState.value[offerTemplateView.value] = {
    ...currentView,
    visibility: updatedVisibility,
  };

  emit("updateField", localState.value);
};

const visibilityControlsOptions = [
  {
    key: VisibilityControl.borrower_platform,
    tooltip: t("OFFER_CUSTOMISATION.TOGGLE_BP_VISIBILITY"),
    label: "BP",
    clickHandler: () => toggleVisibility(VisibilityControl.borrower_platform),
  },
  {
    key: VisibilityControl.workflow_live,
    tooltip: t("OFFER_CUSTOMISATION.TOGGLE_WL_VISIBILITY"),
    label: "WL",
    clickHandler: () => toggleVisibility(VisibilityControl.workflow_live),
  },
];

const updateFieldLabel = (newLabel: string): void => {
  const isDefaultCustomField =
    localState.value[offerTemplateView.value].label ===
    CUSTOM_FIELD_DEFAULT_NAME;
  const finalLabel =
    newLabel ||
    (isDefaultCustomField
      ? CUSTOM_FIELD_DEFAULT_NAME
      : activePlaceholder.value);

  if (isDefaultCustomField) {
    // Update labels for both CLOSING and OFFER views when it's a custom field with default name
    localState.value[OfferTemplateView.CLOSING].label = finalLabel;
    localState.value[OfferTemplateView.OFFER].label = finalLabel;
  } else {
    // Update label only for the current view
    localState.value[offerTemplateView.value].label = finalLabel;
  }

  emit("updateField", localState.value);
};

onMounted(() => {
  placeholders.value = {
    [OfferTemplateView.OFFER]: props.offerField.offer.label,
    [OfferTemplateView.CLOSING]: props.offerField.closing.label,
  };
});
</script>
